import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import deviceManagementService from '../../services/deviceManagementService';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Map as MapIcon,
  Refresh as RefreshIcon,
  MyLocation as LocationIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

// Fix for default marker icon in React Leaflet webpack issues
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

L.Marker.prototype.options.icon = DefaultIcon;

const GeofencingPage = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default center (India roughly)
  const center = [22.0, 79.0];

  const fetchLocations = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const response = await deviceManagementService.getUniqueDevices();
      setDevices(response.devices || response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations(true);
    const interval = setInterval(() => fetchLocations(false), 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  return (
    <Box p={3} sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }} className="animate-fade-in">
      {/* Actions — no page title, the top bar already names the page */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <Stack direction="row" spacing={2}>
          <Tooltip title="Refresh Locations">
            <IconButton onClick={() => fetchLocations(true)} sx={{ bgcolor: 'white', border: '1px solid rgba(0,0,0,0.05)' }}>
              <RefreshIcon color="primary" />
            </IconButton>
          </Tooltip>
          <Chip
            icon={<LocationIcon sx={{ fontSize: '16px !important' }} />}
            label={`${devices.length} Units Active`}
            color="primary"
            sx={{ fontWeight: 700, borderRadius: '8px' }}
          />
        </Stack>
      </Box>

      {/* Map Container */}
      <Card sx={{ flexGrow: 1, borderRadius: '24px', overflow: 'hidden', border: 'none', boxShadow: 'var(--shadow-xl)', position: 'relative', zIndex: 0 }}>
        <MapContainer center={center} zoom={5} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {devices.map(device => {
            const lat = parseFloat(device.latitude || device.location?.lat);
            const lng = parseFloat(device.longitude || device.location?.lon);
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              return (
                <Marker key={device.device_id} position={[lat, lng]}>
                  <Popup>
                    <Box p={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{device.device_id}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                        {device.device_type || device.type}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Chip label="On-site" size="small" color="success" sx={{ height: 16, fontSize: '9px', fontWeight: 900 }} />
                        {device.payload?.fuel_level < 20 && (
                          <Chip label="Low Fuel" size="small" color="error" icon={<WarningIcon sx={{ fontSize: '10px !important' }} />} sx={{ height: 16, fontSize: '9px', fontWeight: 900 }} />
                        )}
                      </Stack>
                    </Box>
                  </Popup>
                </Marker>
              );
            }
            return null;
          })}
        </MapContainer>
      </Card>
    </Box>
  );
};

export default GeofencingPage;
